import React, { useState, useEffect } from 'react';
import { Target, Plus, ChevronRight } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { CareerGoalForm } from './CareerGoalForm';
import { toast } from 'react-hot-toast';
import { LoadingSpinner } from '../common/LoadingSpinner';

interface CareerGoal {
  id: string;
  title: string;
  type: string;
  target_date: string | null;
  status: 'active' | 'completed' | 'archived';
  progress: number;
}

export function CareerGoalTracker() {
  const [goals, setGoals] = useState<CareerGoal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const loadGoals = async () => {
    try {
      const { data, error } = await supabase
        .from('career_goals')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setGoals(data || []);
    } catch (error) {
      console.error('Error loading goals:', error);
      toast.error('Failed to load career goals');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadGoals();
  }, []);

  const updateProgress = async (goalId: string, progress: number) => {
    try {
      const { error } = await supabase
        .from('career_goals')
        .update({ progress })
        .eq('id', goalId);

      if (error) throw error;
      
      setGoals(prev => 
        prev.map(goal => 
          goal.id === goalId ? { ...goal, progress } : goal
        )
      );
    } catch (error) {
      console.error('Error updating progress:', error);
      toast.error('Failed to update progress');
    }
  };

  const getGoalTypeLabel = (type: string) => {
    switch (type) {
      case 'promotion': return 'Promotion';
      case 'transition': return 'Career Transition';
      case 'remote': return 'Remote Work';
      case 'consulting': return 'Consulting';
      case 'leadership': return 'Leadership';
      default: return type;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-[#9d9a98] rounded-xl p-6 shadow-lg">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="bg-[#9d9a98] rounded-xl p-6 shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Target className="h-6 w-6 text-[#800020] mr-2" />
          <h2 className="text-xl font-semibold text-black">Career Goals</h2>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center text-[#800020] hover:opacity-80"
        >
          <Plus className="h-5 w-5 mr-1" />
          <span>Add Goal</span>
        </button>
      </div>

      {goals.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-black mb-4">No career goals set yet. Start by adding your primary goal.</p>
          <button
            onClick={() => setShowForm(true)}
            className="px-6 py-3 bg-[#2a1114] text-white rounded-xl hover:opacity-90 transition-colors inline-flex items-center"
          >
            <Plus className="h-5 w-5 mr-2" />
            Set Primary Goal
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {goals.map((goal) => (
            <div key={goal.id} className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="px-3 py-1 bg-[#2a1114] text-white text-sm rounded-full">
                  {getGoalTypeLabel(goal.type)}
                </span>
                {goal.target_date && (
                  <span className="text-sm text-gray-500">
                    Target: {new Date(goal.target_date).toLocaleDateString()}
                  </span>
                )}
              </div>
              
              <h3 className="text-lg font-medium text-black mb-3">{goal.title}</h3>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-black">Progress</span>
                  <span className="text-black">{goal.progress}%</span>
                </div>
                <div className="relative">
                  <input
                    type="range"
                    value={goal.progress}
                    onChange={(e) => updateProgress(goal.id, parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#800020]"
                    min="0"
                    max="100"
                    step="5"
                  />
                  <div 
                    className="absolute left-0 top-0 h-2 bg-[#800020] rounded-full"
                    style={{ width: `${goal.progress}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <CareerGoalForm
          onClose={() => setShowForm(false)}
          onSuccess={loadGoals}
        />
      )}
    </div>
  );
}